import { compact, escapeRegExp, intersection } from 'lodash';
import Participant from '../../models/participant';

export enum FilterableRoles {
  COACH = 'coach',
  COMMISSIONER = 'commissioner',
  LEAGUE_OWNER = 'league owner',
  MANAGER = 'manager',
  NON_PLAYER = 'non-player',
  PLAYER = 'player',
  TEAM_OWNER = 'team owner',
  MANAGER_PLAYER = 'manager and player',
}

export enum PaymentStatuses {
  PAID = 'paid',
  UNPAID = 'unpaid',
  SCHEDULED = 'scheduled',
  REFUNDED = 'refunded',
  FAILED = 'failed',
  NO_FEES = 'no fees',
}

export const matchesDivision = (participant: Participant, divisionIds: string[]) =>
  participant.seasonParticipants.some(({ division }) =>
    divisionIds.some((divisionId) => +division?.id === +divisionId)
  );

export const matchesGender = (participant: Participant, value: ('Male' | 'Female' | 'Other' | 'Unknown')[]) => {
  // If all are allowed, return all. If none are allowed, the filter is off so return all.
  if (value.length === 4 || value.length === 0) {
    return true;
  }

  let isAllowed = false;

  if (value.includes('Other')) {
    isAllowed =
      isAllowed ||
      (!!participant.gender &&
        participant.gender.toLowerCase() !== 'male' &&
        participant.gender.toLowerCase() !== 'female');
  }

  if (value.includes('Male')) {
    isAllowed = isAllowed || (!!participant.gender && participant.gender.toLowerCase() === 'male');
  }

  if (value.includes('Female')) {
    isAllowed = isAllowed || (!!participant.gender && participant.gender.toLowerCase() === 'female');
  }

  if (value.includes('Unknown')) {
    isAllowed = isAllowed || !participant.gender;
  }

  return isAllowed;
};

export const matchesTeam = (participant: Participant, value: []) => {
  if (value.length > 0) {
    const teamIds = participant.seasonParticipants.map((data) => {
      if (data.team && data.team.id) {
        return data.team.id;
      } else {
        return '[No Team]';
      }
    });
    return intersection(compact(teamIds), value).length > 0;
  }
  return true;
};

export const matchesBirthdate = (participant: Participant, value: { kind: string; value: any }) => {
  if (value.kind === 'years') {
    return participant?.birthdate ? value.value.includes(participant?.birthdate.split('-')[0]) : false;
  } else if (value.kind === 'range') {
    if (!participant.birthdate) {
      return false;
    }
    let participantBirthdate = new Date(participant.birthdate);
    participantBirthdate = new Date(participantBirthdate.getTime() + participantBirthdate.getTimezoneOffset() * 60000);

    participantBirthdate.setHours(0);

    if (value.value.from && !value.value.to) {
      return value.value.from <= participantBirthdate;
    } else if (!value.value.from && value.value.to) {
      return participantBirthdate <= value.value.to;
    }
    return value.value.from <= participantBirthdate && participantBirthdate <= value.value.to;
  } else {
    return !participant.birthdate;
  }
};

export const matchesRole = (participant: Participant, value: FilterableRoles[]) => {
  const participantRoles = participant?.seasonParticipants
    .flatMap((p) => p.participationRoles?.map((r) => Object.values(r)).flat())
    .map((r: string) => r?.toLowerCase());

  return value.reduce((acc, v) => acc || participantRoles.includes(v), false);
};

export const matchesRegistration = (participant: Participant, value: []) => {
  if (value.length > 0) {
    const registrationIds = participant.seasonParticipants.map((data) => {
      if (data.registration && data.registration.registrationFormId) {
        return data.registration.registrationFormId;
      } else {
        return '[No Reg Form]';
      }
    });

    return intersection(compact(registrationIds), value).length > 0;
  }
  return true;
};

export const matchesPaymentStatus = (participant: Participant, value: []) => {
  // May need to be updated for what returns back from the data.
  if (value.length > 0) {
    const registrationPaymentStatuses = participant.seasonParticipants.map((data) => {
      if (data.registration && data.registration?.registrationPaymentStatus) {
        return data.registration?.registrationPaymentStatus.toLowerCase().trim();
      }
    });

    return intersection(compact(registrationPaymentStatuses), value).length > 0;
  }
  return true;
};

export const matchesNameOrEmail = (
  participant: Participant & { contacts: { names: string[], emailAddresses: string[] } },
  value: string
) => {
  const searchTermRegExp = new RegExp(escapeRegExp(value), 'i');
  return (
    `${participant.firstName} ${participant.lastName}`.match(searchTermRegExp) !== null ||
    participant.contacts.names.some(x => x.match(searchTermRegExp) !== null) ||
    participant.emailAddresses.some((emailAddress: any) => emailAddress.email.match(searchTermRegExp)) ||
    participant.contacts.emailAddresses.some(x => x.match(searchTermRegExp))
  );
};
